"use client"

import { useState, useEffect } from "react"
import { Card, Col, Row, Spin, Select, Form, message, Button, Dropdown, Menu, Switch, Tabs } from "antd"
import { ExportToCsv } from "export-to-csv"
import * as XLSX from "xlsx"
import {
  getComboFillForImport,
  importDefinitionService,
  downloadImportDataService,
  verifyAndImportService,
} from "../../services/generic"
import { LoadingOutlined } from "@ant-design/icons"
import verifyError from "../../assets/images/verifyerror.svg"
import TableForImport from "./TableForImport"
import ExcelTableForImport from "./ExcelTableForImport"
import "./index.css"

const { Option } = Select
const { TabPane } = Tabs

const Import = (props) => {
  const propsData = props
  let importTypeFlag = false

  const [switcher, setSwitcher] = useState(false)
  const [selectedId, setSelectedId] = useState([])
  const [importTypeDropdownDetails, setImporTypeDropdownDetails] = useState([])
  const [sortedTableHeader, setSortedTableHeader] = useState([])
  const [tableHeader, setTableHeader] = useState([])
  const [tableData, setTableData] = useState([])
  const [tableDataCopy, setTableDataCopy] = useState([])
  const [templateId, setTemplateId] = useState()
  const [templateColumns, setTemplateColumns] = useState([])
  const [selectedImportName, setSelectedImportName] = useState([])
  const [loading, setLoading] = useState(false)
  const [downloadTemplateFlag, setDownloadTemplateFlag] = useState(false)
  const [showDownloadTemplateOption, setShowDownloadTemplateOption] = useState(false)
  const [verifyAndImportFlag, setVerifyAndImportFlag] = useState(false)
  const [errorDownload, setErrorDownload] = useState(false)
  const [showSwitch, setShowSwitch] = useState(true)

  // Excel specific states
  const [isExcelFile, setIsExcelFile] = useState(false)
  const [excelData, setExcelData] = useState({})
  const [excelHeaders, setExcelHeaders] = useState({})
  const [activeSheet, setActiveSheet] = useState("")
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [rowSelectionKeys, setRowSelectionKeys] = useState([])

  const [form] = Form.useForm()

  useEffect(() => {
    if (propsData.importData !== undefined) {
      const { importData } = propsData
      const { importFlag, importId, windowName } = importData
      onSelectImportData(importId, { children: windowName })
    }
  }, [propsData])

  if (propsData.importData !== undefined) {
    const { importData } = propsData
    const { importFlag } = importData
    importTypeFlag = importFlag
  }

  const getImportDropdownData = async () => {
    const getImportData = await getComboFillForImport()
    setImporTypeDropdownDetails([...getImportData])
  }

  const onSelectImportData = async (value, event) => {
    setLoading(true)
    setTemplateId(value)
    const importTypeId = value
    const importName = event.children

    try {
      const getImportDataOnSelect = await importDefinitionService(importTypeId, importTypeFlag)

      // Check if importDefinition is null or empty - switch to Excel mode
      if (!getImportDataOnSelect || getImportDataOnSelect.length === 0) {
        setLoading(false)
        setSelectedId(importTypeId)
        setSelectedImportName(importName)
        setIsExcelFile(true) // Force Excel mode
        setShowSwitch(false) // Hide the data configuration switch
        setDownloadTemplateFlag(false)
        setVerifyAndImportFlag(false)
        setShowDownloadTemplateOption(false)

        message.info(`No CSV template found for ${importName}. Please upload an Excel file.`)

        // Clear any existing data
        setTableData([])
        setTableHeader([])
        setExcelData({})
        setExcelHeaders({})
        setActiveSheet("")

        return
      }

      // Normal CSV processing when importDefinition exists
      const headerArray = []
      const headerData = Object.keys(getImportDataOnSelect[0])

      for (let index = 0; index < headerData.length; index++) {
        const element = headerData[index]
        headerArray.push({
          title: element,
          dataIndex: element,
          key: index,
          width: 180,
          ellipsis: true,
        })
      }

      setDownloadTemplateFlag(false)
      setShowSwitch(true)
      setIsExcelFile(false) // Ensure we're in CSV mode
      setTableHeader(headerArray)
      setTableData(getImportDataOnSelect)
      setSelectedId(importTypeId)
      setSelectedImportName(importName)

      const headersData = await downloadImportDataService(importTypeId, importTypeFlag)
      const arrayOfColumns = []
      const nullArray = []
      const withoutNullArray = []
      const sortedFieldArray = []
      const parsedResponse = headersData
      const sortedResponse = parsedResponse.sort((a, b) => a.sequenceNo - b.sequenceNo)

      for (let index1 = 0; index1 < sortedResponse.length; index1++) {
        if (sortedResponse[index1].sequenceNo === null) {
          nullArray.push(sortedResponse[index1])
        } else {
          withoutNullArray.push(sortedResponse[index1])
        }
      }

      const finalArray = withoutNullArray.concat(nullArray)
      for (let index = 0; index < finalArray.length; index += 1) {
        const element = finalArray[index].fieldName
        sortedFieldArray.push(finalArray[index].fieldName)
        arrayOfColumns.push({ title: element, dataIndex: element, width: 180, ellipsis: true })
      }

      setTemplateColumns(arrayOfColumns)
      setLoading(false)
    } catch (error) {
      console.error("Error in onSelectImportData:", error)
      setLoading(false)

      // On error, also switch to Excel mode as fallback
      setSelectedId(importTypeId)
      setSelectedImportName(importName)
      setIsExcelFile(true)
      setShowSwitch(false)
      setDownloadTemplateFlag(false)
      setVerifyAndImportFlag(false)
      setShowDownloadTemplateOption(false)

      message.warning(`Error loading CSV template for ${importName}. Please upload an Excel file instead.`)

      // Clear any existing data
      setTableData([])
      setTableHeader([])
      setExcelData({})
      setExcelHeaders({})
      setActiveSheet("")
    }
  }

  const processExcelFile = async (file) => {
    try {
      const bytes = await file.arrayBuffer()
      const buffer = Buffer.from(bytes)
      const workbook = XLSX.read(buffer, { type: "buffer" })
      const sheetNames = workbook.SheetNames

      const sheetsData = {}
      const sheetsHeaders = {}

      // Get template columns for consistent structure
      const headersData = await downloadImportDataService(selectedId, importTypeFlag)
      const arrayOfColumns = []
      const nullArray = []
      const withoutNullArray = []
      const sortedFieldArray = []
      const parsedResponse = headersData
      const sortedResponse = parsedResponse.sort((a, b) => a.sequenceNo - b.sequenceNo)

      for (let index1 = 0; index1 < sortedResponse.length; index1++) {
        if (sortedResponse[index1].sequenceNo === null) {
          nullArray.push(sortedResponse[index1])
        } else {
          withoutNullArray.push(sortedResponse[index1])
        }
      }

      const finalArray = withoutNullArray.concat(nullArray)
      for (let index = 0; index < finalArray.length; index += 1) {
        const element = finalArray[index].fieldName
        sortedFieldArray.push(finalArray[index].fieldName)
        arrayOfColumns.push({ title: element, dataIndex: element, width: 180, ellipsis: true })
      }

      // Process each sheet
      sheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: null,
        })

        if (jsonData.length > 0) {
          const headers = jsonData[0]
          const sheetColumns = []

          // Create columns based on Excel headers
          headers.forEach((header, index) => {
            if (header) {
              sheetColumns.push({
                title: header,
                dataIndex: header,
                key: index,
                width: 180,
                ellipsis: true,
              })
            }
          })

          // Process data rows
          const processedData = []
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i]

            // Skip empty rows - check if all cells are empty
            const isEmptyRow = row.every((cell) => cell === null || cell === undefined || cell === "")
            if (isEmptyRow) continue

            const jsonObject = {}
            let hasError = false

            headers.forEach((header, colIndex) => {
              if (header) {
                const cellValue = row[colIndex] ?? ""
                jsonObject[header] = cellValue

                if (cellValue === "$%&^*") {
                  hasError = true
                }
              }
            })

            // Check if the object has any non-empty values (excluding status and key)
            const hasData = Object.values(jsonObject).some(
              (value) => value !== null && value !== undefined && value !== "",
            )

            if (hasData) {
              jsonObject.status = hasError ? "Has Error" : "No Error"
              jsonObject.key = processedData.length // Use the current length for proper sequential keys
              processedData.push(jsonObject)
            }
          }

          // Add status column
          sheetColumns.push({
            title: "Status",
            dataIndex: "status",
            width: 180,
            ellipsis: true,
          })

          sheetsData[sheetName] = processedData
          sheetsHeaders[sheetName] = sheetColumns
        }
      })

      setExcelData(sheetsData)
      setExcelHeaders(sheetsHeaders)
      setActiveSheet(sheetNames[0])
      setSortedTableHeader(sortedFieldArray)
      setTemplateColumns(arrayOfColumns)

      return { sheetsData, sheetsHeaders, sheetNames }
    } catch (error) {
      console.error("Excel processing error:", error)
      throw error
    }
  }

  const readFileData = async (evt) => {
    setSelectedRowKeys([])
    setRowSelectionKeys([])
    setTableHeader([])
    setTableData([])
    setExcelData({})
    setExcelHeaders({})
    setIsExcelFile(false)
    setLoading(true)

    const { files } = evt.target
    if (files[0] === undefined) {
      setLoading(false)
      document.getElementById("choosefile").value = null
      return
    }

    const filename = files[0].name
    const fileExt = filename.substr(filename.lastIndexOf("."), filename.length)

    // Check if it's an Excel file
    if (fileExt === ".xlsx" || fileExt === ".xls") {
      setIsExcelFile(true)

      try {
        const result = await processExcelFile(files[0])
        setLoading(false)
        message.success("Excel file imported successfully")
        setShowDownloadTemplateOption(true)
        setDownloadTemplateFlag(true)
        setVerifyAndImportFlag(true)
      } catch (error) {
        message.error("Error processing Excel file")
        setLoading(false)
        document.getElementById("choosefile").value = null
      }
      return
    }

    // Existing CSV processing logic
    if (fileExt !== ".csv") {
      message.error("Please select csv or excel file")
      setLoading(false)
      return
    }

    // Rest of the existing CSV processing code remains unchanged
    const headersData = await downloadImportDataService(selectedId, importTypeFlag)
    const arrayOfColumns = []
    const nullArray = []
    const withoutNullArray = []
    const sortedFieldArray = []
    const parsedResponse = headersData
    const sortedResponse = parsedResponse.sort((a, b) => a.sequenceNo - b.sequenceNo)

    for (let index1 = 0; index1 < sortedResponse.length; index1++) {
      if (sortedResponse[index1].sequenceNo === null) {
        nullArray.push(sortedResponse[index1])
      } else {
        withoutNullArray.push(sortedResponse[index1])
      }
    }

    const finalArray = withoutNullArray.concat(nullArray)
    for (let index = 0; index < finalArray.length; index += 1) {
      const element = finalArray[index].fieldName
      sortedFieldArray.push(finalArray[index].fieldName)
      arrayOfColumns.push({ title: element, dataIndex: element, width: 180, ellipsis: true })
    }

    setTableHeader(arrayOfColumns)
    setSortedTableHeader(sortedFieldArray)
    setTemplateColumns(arrayOfColumns)

    const file = files[0]
    const reader = new FileReader()
    let fileTextData

    reader.onload = (e) => {
      fileTextData = e.target.result
      const rows = fileTextData.split(/\r?\n/)
      const newData = []

      rows.forEach((element) => {
        if (element.trim() !== "") {
          newData.push(element)
        }
      })

      const dataArr = []
      const importCount = 1

      for (let i = 1; i < newData.length; i += 1) {
        const row = newData[i]
        const cells = row.split(",")
        const jsonObject = {}
        let hasError = false

        for (let index = 0; index < arrayOfColumns.length; index += 1) {
          const element = arrayOfColumns[index]
          const cellValue = cells[index]?.replace(/"/g, "") ?? ""
          jsonObject[element.dataIndex] = cellValue

          if (cellValue === "$%&^*") {
            hasError = true
            break
          }
        }

        jsonObject.status = hasError ? "Has Error" : "No Error"
        dataArr.push(jsonObject)
      }

      const tableDataWithoutStatus = dataArr.map(({ status, ...rest }) => rest)
      setTableDataCopy(tableDataWithoutStatus)

      arrayOfColumns.push({ title: "Status", dataIndex: "status", width: 180, ellipsis: true })

      const data = dataArr.map((item, index) => ({
        ...item,
        key: index,
      }))

      if (data.length > 0) {
        setTableData(data)
        setLoading(false)
        message.success("Data imported successfully")
        setShowDownloadTemplateOption(true)
        setDownloadTemplateFlag(true)
        setVerifyAndImportFlag(true)
      } else {
        message.error("Error in importing file")
        document.getElementById("choosefile").value = null
        setLoading(false)
      }
    }

    reader.readAsText(file)
  }

  const clearData = () => {
    form.resetFields()
    setTableData([])
    setTableHeader([])
    setExcelData({})
    setExcelHeaders({})
    setIsExcelFile(false)
    setActiveSheet("")
    setShowDownloadTemplateOption(false)
    setDownloadTemplateFlag(false)
    setVerifyAndImportFlag(false)
    document.getElementById("choosefile").value = null
  }

  const verifyAndImport = () => {
    let hasImportErrors = false

    if (isExcelFile) {
      // Check errors across all Excel sheets
      Object.values(excelData).forEach((sheetData) => {
        if (sheetData.some((record) => record.status === "Has Error")) {
          hasImportErrors = true
        }
      })
    } else {
      hasImportErrors = tableData.some((record) => record.status === "Has Error")
    }

    if (hasImportErrors) {
      message.warn("some of your records have errors")
    } else {
      form.submit()
    }
  }

  const onFinish = async (values) => {
    let dataToSend = {}

    if (isExcelFile) {
      // Structure data as sheet name -> array of objects
      Object.keys(excelData).forEach((sheetName) => {
        // Filter out empty rows and remove status and key properties
        const sheetDataWithoutStatus = excelData[sheetName]
          .filter((row) =>
            Object.entries(row)
              .filter(([key]) => key !== "status" && key !== "key")
              .some(([_, value]) => value !== null && value !== undefined && value !== ""),
          )
          .map(({ status, key, ...rest }) => rest)

        // Only add the sheet if it has data
        if (sheetDataWithoutStatus.length > 0) {
          dataToSend[sheetName] = sheetDataWithoutStatus
        }
      })
    } else {
      // For CSV, use the existing approach but filter out empty objects
      const filteredData = tableDataCopy.filter((obj) =>
        Object.values(obj).some((val) => val !== null && val !== undefined && val !== ""),
      )
      dataToSend = filteredData
    }

    if (propsData.importData !== undefined) {
      const { importData } = propsData
      const { headerId } = importData
      setLoading(true)
      const tableHeaderAfterVerifyAndImport = []

      // Convert to string for the API
      const stringifiedJSON = JSON.stringify(dataToSend)
      const jsonToSend = stringifiedJSON.replace(/"/g, '\\"')

      const verifyAndImportResponse = await verifyAndImportService(
        headerId,
        selectedId,
        jsonToSend,
        templateId,
        importTypeFlag,
      )

      if (!verifyAndImportResponse || !verifyAndImportResponse.afterInsert) {
        setLoading(false)
        message.error("Unable to Verify and Import the data")
        return
      }

      const { afterInsert } = verifyAndImportResponse
      const messageCodeAndMessageArray = ["ErrorMessageCode", "ErrorMessage"]
      const verifyAndImportTableHeader = messageCodeAndMessageArray.concat(sortedTableHeader)

      for (let index = 0; index < verifyAndImportTableHeader.length; index++) {
        const element = verifyAndImportTableHeader[index]
        tableHeaderAfterVerifyAndImport.push({
          title: element,
          render: (text) =>
            text === "Verify Error" ? (
              <span>
                {text}&emsp;
                <img alt="" src={verifyError || "/placeholder.svg"} />
              </span>
            ) : (
              text
            ),
          dataIndex: element,
          width: 180,
          ellipsis: true,
        })
      }

      const hasImportErrors = afterInsert.some(
        (record) => record.ErrorMessageCode === "Import Error" || record.ErrorMessageCode === "Verify Error",
      )
      setErrorDownload(hasImportErrors)
      setTableHeader(tableHeaderAfterVerifyAndImport)
      setTableData(afterInsert)
      setLoading(false)
      setVerifyAndImportFlag(false)
      setIsExcelFile(false) // Reset to show regular table
    } else {
      // Similar logic for non-propsData case
      setLoading(true)
      const headerId = null
      const tableHeaderAfterVerifyAndImport = []

   const stringifiedJSON = JSON.stringify(dataToSend).replace(/\\/g, "\\\\").replace(/"/g, '\\"')

      const verifyAndImportResponse = await verifyAndImportService(headerId, selectedId, stringifiedJSON, templateId)
         if (isExcelFile) {
    setLoading(false);
    return; // Exit early if Excel file — no need to proceed
  }

      const { afterInsert } = verifyAndImportResponse

      const messageCodeAndMessageArray = ["ErrorMessageCode", "ErrorMessage"]
      const verifyAndImportTableHeader = messageCodeAndMessageArray.concat(sortedTableHeader)

      for (let index = 0; index < verifyAndImportTableHeader.length; index++) {
        const element = verifyAndImportTableHeader[index]
        tableHeaderAfterVerifyAndImport.push({
          title: element,
          render: (text) =>
            text === "Verify Error" ? (
              <span>
                {text}&emsp;
                <img alt="" src={verifyError || "/placeholder.svg"} />
              </span>
            ) : (
              text
            ),
          dataIndex: element,
          width: 180,
          ellipsis: true,
        })
      }

      const hasImportErrors = afterInsert.some(
        (record) => record.ErrorMessageCode === "Import Error" || record.ErrorMessageCode === "Verify Error",
      )
      setErrorDownload(hasImportErrors)
      setTableHeader(tableHeaderAfterVerifyAndImport)
      setTableData(afterInsert)
      setLoading(false)
      setVerifyAndImportFlag(false)
      setIsExcelFile(false) // Reset to show regular table
    }
  }

  const onFinishFailed = (errorInfo) => {
    console.log(errorInfo)
  }

  // Rest of the existing functions remain unchanged
  const downloadTemplate = () => {
    if (isExcelFile) {
      // For Excel mode, we might not have templateColumns, so create a basic template
      message.info("Please upload your Excel file with the appropriate column structure.")
      return
    }

    if (templateColumns === undefined || templateColumns === null || templateColumns.length === 0) {
      message.error("No Headers present")
    } else {
      const templateArray = []
      const headerArray = []
      for (let index = 0; index < templateColumns.length; index += 1) {
        const element = templateColumns[index]
        const fieldName = element.dataIndex
        templateArray.push({})
        headerArray.push(fieldName)
      }
      const options = {
        fieldSeparator: ",",
        filename: selectedImportName,
        decimalSeparator: ".",
        showLabels: true,
        showTitle: false,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: false,
        headers: headerArray,
      }
      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(templateArray)
    }
  }

  const downloadData = () => {
    let dataToDownload = []
    let headersToUse = []

    if (isExcelFile && activeSheet) {
      dataToDownload = excelData[activeSheet] || []
      headersToUse = excelHeaders[activeSheet] || []
    } else {
      dataToDownload = tableData
      headersToUse = tableHeader
    }

    if (dataToDownload.length === 0 || headersToUse.length === 0) {
      message.error("No Headers present")
    } else {
      for (let index = 0; index < dataToDownload.length; index += 1) {
        const element = dataToDownload[index]
        Object.keys(element).forEach((key) => {
          const Keys = key
          if (Keys.substring(0, 2) === "__") {
            delete element[Keys]
          }
        })
      }
      const options = {
        fieldSeparator: ",",
        filename: "ImportData",
        quoteStrings: '"',
        decimalSeparator: ".",
        showLabels: true,
        showTitle: false,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
        headers: headersToUse,
      }
      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(dataToDownload)
    }
  }

  const downloadErrorRecords = () => {
    let dataToDownload = []
    let headersToUse = []

    if (isExcelFile && activeSheet) {
      dataToDownload = excelData[activeSheet] || []
      headersToUse = excelHeaders[activeSheet] || []
    } else {
      dataToDownload = tableData
      headersToUse = tableHeader
    }

    if (dataToDownload.length === 0 || headersToUse.length === 0) {
      message.error("No Headers present")
    } else {
      for (let index = 0; index < dataToDownload.length; index += 1) {
        const element = dataToDownload[index]
        Object.keys(element).forEach((key) => {
          const Keys = key
          if (Keys.substring(0, 2) === "__") {
            delete element[Keys]
          }
        })
      }
      const importErrorRecords = dataToDownload.filter(
        (record) => record.ErrorMessageCode === "Import Error" || record.ErrorMessageCode === "Verify Error",
      )
      const options = {
        fieldSeparator: ",",
        filename: "ImportData",
        quoteStrings: '"',
        decimalSeparator: ".",
        showLabels: true,
        showTitle: false,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
        headers: headersToUse,
      }
      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(importErrorRecords)
    }
  }

  const actionsContent = (
    <Menu>
      {showDownloadTemplateOption === true ? (
        <Menu.Item key="1" style={{ color: "#314659" }} onClick={downloadTemplate}>
          Download Template
        </Menu.Item>
      ) : (
        ""
      )}
      {downloadTemplateFlag === true ? (
        <Menu.Item key="2" style={{ color: "#314659" }} onClick={downloadErrorRecords} disabled={!errorDownload}>
          Download Error Records
        </Menu.Item>
      ) : (
        ""
      )}
      <Menu.Item key="4" style={{ color: "#314659" }} onClick={clearData}>
        Clear
      </Menu.Item>
    </Menu>
  )

  const onSelectChange = (keys, selectedRows) => {
    console.log(keys, selectedRows)
    setRowSelectionKeys([...keys])
    setSelectedRowKeys([...selectedRows])
  }

  const rowSelection = {
    selectedRowKeys: rowSelectionKeys,
    onChange: onSelectChange,
    hideSelectAll: true,
    fixed: true,
  }

  const columns = tableHeader.map((col, index) => ({
    ...col,
    onHeaderCell: (columns) => ({
      width: columns.width,
      onResize: handleResize(index),
    }),
  }))

  const handleResize =
    (index) =>
    (e, { size }) => {
      setTableHeader((columns) => {
        const nextColumns = [...columns]
        nextColumns[index] = {
          ...nextColumns[index],
          width: size.width,
        }
        return nextColumns
      })
    }

  const deleteSelectedRecords = () => {
    if (isExcelFile && activeSheet) {
      const updatedSheetData = excelData[activeSheet]
        .filter((item, index) => !rowSelectionKeys.includes(index))
        .map((item, index) => {
          const { key, ...rest } = item
          return { key: index, ...rest }
        })

      setExcelData((prev) => ({
        ...prev,
        [activeSheet]: updatedSheetData,
      }))
    } else {
      const updatedTableData = tableData
        .filter((item, index) => !rowSelectionKeys.includes(index))
        .map((item, index) => {
          const { key, ...rest } = item
          return { key: index, ...rest }
        })

      const updatedTableDataWithoutStatus = updatedTableData.map(({ status, ...rest }) => rest)
      setTableDataCopy(updatedTableDataWithoutStatus)
      setTableData(updatedTableData)
    }

    setSelectedRowKeys([])
    setRowSelectionKeys([])
  }

  const renderExcelTabs = () => {
    if (!isExcelFile || Object.keys(excelData).length === 0) return null

    return (
      <Tabs activeKey={activeSheet} onChange={setActiveSheet}>
        {Object.keys(excelData).map((sheetName) => (
          <TabPane tab={sheetName} key={sheetName}>
            <ExcelTableForImport
              columnsData={excelHeaders[sheetName] || []}
              gridData={excelData[sheetName] || []}
              rowSelection={rowSelection}
              sheetName={sheetName}
            />
          </TabPane>
        ))}
      </Tabs>
    )
  }

  return (
    <div>
      <Spin indicator={<LoadingOutlined style={{ fontSize: "52px" }} spin />} spinning={loading}>
        <Row gutter={8}>
          <Col span={24} style={{ marginBottom: "8px" }}>
            <Card style={{ paddingBottom: 0 }}>
              <Form form={form} layout="vertical" onFinish={onFinish} onFinishFailed={onFinishFailed}>
                <Row gutter={8}>
                  {importTypeFlag === false ? (
                    <Col span={6}>
                      <Form.Item
                        label="Import"
                        name="import"
                        rules={[{ required: true, message: "Please select supplier" }]}
                      >
                        <Select
                          style={{ width: "100%" }}
                          size="medium"
                          maxTagCount={1}
                          showSearch
                          allowClear
                          dropdownMatchSelectWidth={false}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          onFocus={getImportDropdownData}
                          onSelect={onSelectImportData}
                        >
                          {importTypeDropdownDetails === null || importTypeDropdownDetails === undefined
                            ? null
                            : importTypeDropdownDetails.map((data) => {
                                return (
                                  <Option key={data.recordid} value={data.recordid}>
                                    {data.name}
                                  </Option>
                                )
                              })}
                        </Select>
                      </Form.Item>
                    </Col>
                  ) : null}
                  <Col span={6}>
                    <Form.Item
                      label="Import Option"
                      name="importOption"
                      rules={[{ required: false, message: "Please select import option" }]}
                    >
                      <Select
                        style={{ width: "100%" }}
                        size="medium"
                        maxTagCount={1}
                        showSearch
                        allowClear
                        dropdownMatchSelectWidth={false}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        <Option key="1" value="Create New">
                          Create New
                        </Option>
                        <Option key="2" value="Update Existing">
                          Update Existing
                        </Option>
                        <Option key="3" value="Create New & Update Existing">
                          Create New & Update Existing
                        </Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <input
                      id="choosefile"
                      type="file"
                      accept={isExcelFile ? ".xlsx,.xls" : ".csv,.xlsx,.xls"}
                      onChange={readFileData}
                      style={{ marginTop: "19%" }}
                    />
                    {isExcelFile && selectedId && (
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                        Excel mode: Upload .xlsx or .xls file
                      </div>
                    )}
                  </Col>
                  <Col span={9} style={{ textAlign: "end" }}>
                    <Dropdown placement="bottomRight" overlay={actionsContent} trigger={["hover"]}>
                      <Button
                        style={{ marginTop: "", backgroundColor: "#089EA4", color: "#FFFFFF", fontWeight: "bold" }}
                      >
                        Actions
                      </Button>
                    </Dropdown>
                    &nbsp;
                    <Button
                      disabled={verifyAndImportFlag === false ? true : false}
                      style={{ marginTop: "5%", backgroundColor: "#089EA4", color: "#FFFFFF", fontWeight: "bold" }}
                      onClick={verifyAndImport}
                    >
                      Verify and Import
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ marginBottom: "8px" }}>
            <Card>
              {downloadTemplateFlag === false ? (
                <span style={{ cursor: "pointer", color: "#0000EE" }} onClick={downloadTemplate}>
                  {isExcelFile ? "Download Excel Template" : "Download Template"}
                </span>
              ) : (
                <span style={{ cursor: "pointer", color: "#0000EE" }} onClick={downloadData}>
                  Download CSV Format
                </span>
              )}
              {selectedRowKeys?.length > 0 ? (
                <span
                  style={{ cursor: "pointer", color: "#0000EE", float: "right", display: "flex" }}
                  onClick={deleteSelectedRecords}
                >
                  Delete Selected Records
                </span>
              ) : (
                ""
              )}
              {downloadTemplateFlag === false && showSwitch && !isExcelFile ? (
                <span style={{ float: "right" }}>
                  <label>Data Configuration</label>
                  <Switch
                    onChange={() => {
                      setSwitcher(!switcher)
                    }}
                  />
                </span>
              ) : null}
              <br />

              {isExcelFile ? (
                renderExcelTabs()
              ) : (
                <TableForImport
                  columnsData={columns}
                  gridData={tableData}
                  rowSelection={rowSelection}
                  switcher={switcher}
                  downloadTemplateFlag={downloadTemplateFlag}
                  setDownloadTemplateFlag={setDownloadTemplateFlag}
                  showSwitch={showSwitch}
                  setShowSwitch={setShowSwitch}
                  setShowDownloadTemplateOption={setShowDownloadTemplateOption}
                  setVerifyAndImportFlag={setVerifyAndImportFlag}
                  setTableDataCopy={setTableDataCopy}
                  setTableData={setTableData}
                  setTableHeader={setTableHeader}
                  setSortedTableHeader={setSortedTableHeader}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}

export default Import
